
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { FilterState } from "../types/filter-types";
import { getExchangeLogo, getInvestmentLabel, getRiskLabel } from "../utils/filter-utils";
import { getCampaignCookie } from "@/hooks/referrals/referralUtils";

interface ActiveFiltersProps {
  filters: FilterState;
  topPerformers: boolean;
  removeFilter: (type: string, value?: string) => void;
}

const ActiveFilters = ({ filters, topPerformers, removeFilter }: ActiveFiltersProps) => {
  const campaignCookie = getCampaignCookie();
  const isPhemexCampaign = campaignCookie === "phemex";

  const getStrategyTypeLabel = (type: "premium" | "free") => {
    return type === "premium" ? (
      <span className="bg-[#F6C528] px-3 py-1 rounded-md text-[#012846] font-semibold text-xs">PREMIUM</span>
    ) : "FREE";
  };

  const hasActiveFilters = 
    topPerformers || 
    filters.minInvestment !== "all" || 
    filters.riskLevel.length > 0 || 
    filters.exchange.length > 0 || 
    filters.strategyTypes.length > 0;

  if (!hasActiveFilters) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-[#4A607B] font-semibold">Selected Filters</h3>
        <div className="h-px flex-grow bg-gray-200"></div>
      </div>
      <div className="flex flex-wrap gap-2">
        {topPerformers && (
          <Badge variant="outline" className="bg-white">
            Top 10 Performers
            <button
              onClick={() => removeFilter("topPerformers")}
              className="ml-2 hover:text-red-500"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
        {filters.minInvestment !== "all" && (
          <Badge variant="outline" className="bg-white">
            {getInvestmentLabel(filters.minInvestment)}
            <button
              onClick={() => removeFilter("minInvestment")}
              className="ml-2 hover:text-red-500"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
        {filters.riskLevel.map((level) => (
          <Badge key={level} variant="outline" className="bg-white">
            {getRiskLabel(level)}
            <button
              onClick={() => removeFilter("riskLevel", level)}
              className="ml-2 hover:text-red-500"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        {filters.exchange.map((ex) => (
          <Badge key={ex} variant="outline" className="bg-white">
            <img 
              src={getExchangeLogo(ex)} 
              alt={ex} 
              className="h-4 w-auto" 
            />
            <button
              onClick={() => removeFilter("exchange", ex)}
              className={`ml-2 hover:text-red-500 ${isPhemexCampaign && ex === "Phemex" ? 'hidden' : ''}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        {filters.strategyTypes.map((type) => (
          <Badge key={type} variant="outline" className="bg-white flex items-center">
            {getStrategyTypeLabel(type)}
            <button
              onClick={() => removeFilter("strategyType", type)}
              className="ml-2 hover:text-red-500"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
};

export default ActiveFilters;
